import { useEffect, useMemo, useState } from "react";

import { Dataset } from "@/types/dataset";
import { debounce } from "@/utils/debounce";

import SliderInput from "./SliderInput";

interface Props {
  data: Dataset;
  max: number;
  onChangeSlider?: (value: number[]) => void;
  onChangeInput?: (value: number) => void;
}

const INPUT_COMMIT_DEBOUNCE_MS = 400;

export const RankSlider = ({
  data,
  max,
  onChangeSlider,
  onChangeInput
}: Props) => {
  /** Committed ranking steps from the store */
  const storedRankingStepValue = data.style.rankConfig?.maxRanking ?? 1;

  /** Local value for display only, commited value will be persisted to Redux store via onChangeInput or onChangeSlider */
  const [localRankingStepValue, setLocalRankingStepValue] = useState<
    number | null
  >(null);

  const displayValue = localRankingStepValue ?? storedRankingStepValue;

  /** useMemo (not useCallback): keep ONE debounced instance — and its timer —
   * across the per-keystroke re-renders. Commits the latest typed value to the
   * store once the user pauses. */
  const debouncedCommitInput = useMemo(
    () =>
      debounce((value: number) => {
        setLocalRankingStepValue(null);
        onChangeInput?.(value);
      }, INPUT_COMMIT_DEBOUNCE_MS),
    [onChangeInput]
  );

  // Cancel a pending commit on unmount.
  useEffect(() => () => debouncedCommitInput.cancel(), [debouncedCommitInput]);

  return (
    <div className="flex flex-col gap-2 w-full">
      <p className="text-xs text-txtPlaceholder">Max Ranking</p>
      <SliderInput
        disabled={!data.style.rankConfig?.isEnabled || max <= 1}
        valueSlider={[displayValue]}
        onChangeSlider={(value) =>
          setLocalRankingStepValue(Math.max(1, value[0]))
        }
        onCommitSlider={(value) => {
          // A drag supersedes any pending input edit, cancel any pending debounce
          debouncedCommitInput.cancel();
          setLocalRankingStepValue(null);
          onChangeSlider?.(value);
        }}
        valueInput={displayValue}
        onChangeInput={(value) => {
          setLocalRankingStepValue(value);
          debouncedCommitInput(value);
        }}
        max={max}
      />
    </div>
  );
};
